import React, { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronDown, Search, Star, X, LogOut, Clock, Users } from 'lucide-react';
import { flattenNav, searchTools } from '../../design-system/adminNavCatalog';

const ToolLink = ({ tool, fav, onToggleFav, onPick, showSection }) => {
  const Ico = tool.icon;
  return (
    <div className="adm-m-item-row">
      <NavLink to={tool.path} end onClick={onPick}
        className={({ isActive }) => `adm-m-item ${isActive ? 'is-active' : ''}`}
        data-testid={`adm-m-link-${tool.path.replace(/\//g, '-')}`}>
        {Ico && <Ico className="adm-m-item-ico" aria-hidden />}
        <span className="adm-m-item-txt">
          <span className="adm-m-item-label">{tool.label}</span>
          {showSection && <span className="adm-m-item-sub">{tool.sectionLabel}{tool.group ? ` · ${tool.group}` : ''}</span>}
        </span>
      </NavLink>
      <button type="button" className={`adm-m-fav ${fav ? 'on' : ''}`} onClick={() => onToggleFav(tool.path)}
        aria-pressed={fav} aria-label={fav ? `Remover ${tool.label} dos favoritos` : `Favoritar ${tool.label}`}
        data-testid={`adm-m-fav-${tool.path.replace(/\//g, '-')}`}>
        <Star className="w-4 h-4" aria-hidden />
      </button>
    </div>
  );
};

const Section = ({ section, open, onToggle, ...rest }) => {
  const Ico = section.icon;
  const more = section.more || [];
  const items = [...section.children, ...(more.length ? [{ divider: true, label: 'MAIS FERRAMENTAS' }] : []), ...more];
  return (
    <div className="adm-m-sec">
      <button type="button" className={`adm-m-sec-btn ${open ? 'open' : ''}`} onClick={onToggle}
        aria-expanded={open} data-testid={`adm-m-sec-${section.id}`}>
        <Ico className="adm-m-item-ico" aria-hidden />
        <span className="adm-m-sec-label">{section.label}</span>
        <ChevronDown className="adm-m-chev" aria-hidden />
      </button>
      {open && (
        <div className="adm-m-sec-body">
          {items.map((c, i) => {
            if (c.divider) return <div key={`d${i}`} className="adm-m-group">{c.label}</div>;
            return <ToolLink key={c.path + i} tool={{ ...c, sectionLabel: section.label }} fav={rest.isFav(c.path)} onToggleFav={rest.toggleFav} onPick={rest.onPick} />;
          })}
        </div>
      )}
    </div>
  );
};

export function AdminMobileDrawer({ open, mode, onClose, sections, prefs, user, onLogout }) {
  const location = useLocation();
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState({});
  const flat = useMemo(() => flattenNav(sections), [sections]);
  const byPath = useMemo(() => Object.fromEntries(flat.map((t) => [t.path, t])), [flat]);
  const results = useMemo(() => searchTools(flat, query), [flat, query]);
  const favTools = prefs.favs.map((p) => byPath[p]).filter(Boolean);
  const recentTools = prefs.recents.map((p) => byPath[p]).filter(Boolean);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    const active = sections.find((s) => [...s.children, ...(s.more || [])].some((c) => c.path && (location.pathname === c.path || location.pathname.startsWith(`${c.path}/`))));
    setExpanded(active ? { [active.id]: true } : {});
    const t = setTimeout(() => (mode === 'search' ? inputRef.current : panelRef.current?.querySelector('[data-autofocus]'))?.focus({ preventScroll: true }), 80);
    return () => clearTimeout(t);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); return; }
      if (e.key !== 'Tab') return;
      const f = panelRef.current?.querySelectorAll('a[href], button:not([disabled]), input');
      if (!f?.length) return;
      const first = f[0]; const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  const common = { isFav: prefs.isFav, toggleFav: prefs.toggleFav, onPick: onClose };
  const onlyFavs = mode === 'favs';

  return (
    <div className={`adm-m-drawer-wrap ${open ? 'is-open' : ''}`} aria-hidden={!open} data-testid="adm-m-drawer-wrap">
      <div className="adm-m-scrim" onClick={onClose} data-testid="adm-m-drawer-scrim" />
      <aside ref={panelRef} id="adm-m-drawer" className="adm-m-drawer" role="dialog" aria-modal="true"
        aria-label="Menu de ferramentas" data-testid="adm-m-drawer" inert={!open}>
        <div className="adm-m-drawer-head">
          <img src="/novo-dna-logo.png" alt="NOVO DNA" className="adm-m-logo" />
          <div className="adm-m-brand"><strong>DNACore</strong><span>NOVO DNA · Instituto de Aprendizagem</span></div>
          <button type="button" className="adm-m-iconbtn" onClick={onClose} aria-label="Fechar menu" data-autofocus data-testid="adm-m-drawer-close">
            <X className="w-5 h-5" aria-hidden />
          </button>
        </div>
        <label className="adm-m-search">
          <Search className="w-4 h-4" aria-hidden />
          <span className="sr-only">Encontrar ferramenta</span>
          <input ref={inputRef} type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Encontrar ferramenta"
            autoComplete="off" enterKeyHint="search" data-testid="adm-m-search-input" />
          {query && <button type="button" onClick={() => setQuery('')} aria-label="Limpar busca" data-testid="adm-m-search-clear"><X className="w-4 h-4" aria-hidden /></button>}
        </label>

        <nav className="adm-m-drawer-body" aria-label="Ferramentas do DNACore" data-testid="adm-m-drawer-body">
          {query ? (
            <div data-testid="adm-m-search-results">
              <div className="adm-m-group" aria-live="polite">{results.length ? `${results.length} ferramenta(s)` : 'Nenhuma ferramenta encontrada'}</div>
              {results.length === 0 && <p className="adm-m-empty" data-testid="adm-m-search-empty">Tente outro nome, como “boleto”, “notas” ou “matrícula”.</p>}
              {results.map((t) => <ToolLink key={t.path} tool={t} fav={prefs.isFav(t.path)} onToggleFav={prefs.toggleFav} onPick={onClose} showSection />)}
            </div>
          ) : (
            <>
              <div className="adm-m-group"><Star className="w-3 h-3" aria-hidden /> FAVORITOS</div>
              {favTools.length === 0 && <p className="adm-m-empty" data-testid="adm-m-favs-empty">Toque na estrela de uma ferramenta para fixá-la aqui.</p>}
              <div data-testid="adm-m-favs">{favTools.map((t) => <ToolLink key={t.path} tool={t} fav onToggleFav={prefs.toggleFav} onPick={onClose} showSection />)}</div>
              {recentTools.length > 0 && (
                <>
                  <div className="adm-m-group"><Clock className="w-3 h-3" aria-hidden /> RECENTES</div>
                  <div data-testid="adm-m-recents">{recentTools.map((t) => <ToolLink key={t.path} tool={t} fav={prefs.isFav(t.path)} onToggleFav={prefs.toggleFav} onPick={onClose} showSection />)}</div>
                </>
              )}
              {!onlyFavs && (
                <>
                  <button type="button" className="adm-m-global" onClick={() => { onClose(); window.dispatchEvent(new CustomEvent('dna:open-search')); }} data-testid="adm-m-global-search">
                    <Users className="w-4 h-4" aria-hidden /> Pesquisar alunos e registros
                  </button>
                  <div className="adm-m-group">TODAS AS FERRAMENTAS</div>
                  {sections.map((s) => (
                    <Section key={s.id} section={s} open={!!expanded[s.id]} onToggle={() => setExpanded((p) => ({ ...p, [s.id]: !p[s.id] }))} {...common} />
                  ))}
                </>
              )}
              {onlyFavs && <button type="button" className="adm-m-global" onClick={() => onClose('menu')} data-testid="adm-m-see-all">Ver todas as ferramentas</button>}
            </>
          )}
        </nav>

        <div className="adm-m-drawer-foot">
          <div className="adm-m-avatar" aria-hidden>{(user.name || user.email || '?').charAt(0).toUpperCase()}</div>
          <div className="adm-m-user"><strong>{user.name || 'Usuário'}</strong><span>{user.email}</span></div>
          <button type="button" className="adm-m-logout" onClick={onLogout} data-testid="adm-m-logout"><LogOut className="w-4 h-4" aria-hidden /> Sair</button>
        </div>
      </aside>
    </div>
  );
}
